/**
 * @name flowUserMenu
 * @module flowingly.user.menu
 * @description A DUMB component is used to display a user menu.
 * ### Notes
 * It is intended that it is rendered by a SMART component that handles interaction with server.
 * Right now, this is the sidemenu.
 * @usage
 * ```
    <flow-user-menu ng-if="ctrl.authenticated"
                    user="ctrl.user"
                    on-logout-method="ctrl.logout()">
        </flow-user-menu>
 * ```
 */
import angular from 'angular';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';

angular.module('flowingly.user.menu').component('flowUserMenu', {
  templateUrl: 'usermenu.tmpl.html',
  bindings: {
    user: '<',
    iconSmall: '<',
    isMobile: '<',
    onLogoutMethod: '&',
    toggleSmartSearch: '&'
  },

  controller: [
    '$state',
    '$scope',
    'dialogService',
    'APP_CONFIG',
    'tokenService',
    'permissionsService',
    'flowinglyConstants',
    'sessionService',
    'webPushService',
    'circleService',
    function (
      $state: angular.ui.IStateService,
      $scope: angular.IScope,
      dialogService: SharedAngular.DialogService,
      APP_CONFIG: SharedAngular.APP_CONFIG,
      tokenService: SharedAngular.TokenService,
      permissionsService: SharedAngular.PermissionsService,
      flowinglyConstants: SharedAngular.FlowinglyConstants,
      sessionService: SharedAngular.SessionService,
      webPushService: WebPushService,
      circleService: CircleService
    ) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const $ctrl = this;

      $ctrl.isCircleLoading = false;
      $ctrl.showCircleCommunity = APP_CONFIG.enableCircleCommunity;
      $ctrl.openCircleCommunity = function () {
        $ctrl.isCircleLoading = true;
        circleService.openCommunity().finally(() => {
          $ctrl.isCircleLoading = false;
        });
      };
      $ctrl.editProfile = editProfile;
      $ctrl.logout = logout;
      $ctrl.showSupportButton = showSupportButton(APP_CONFIG.hideSupportButton);
      $ctrl.enableTenantSwitching = APP_CONFIG.enableTenantSwitching;
      $ctrl.workflowsAreEnabled = APP_CONFIG.allowWorkflow;
      $ctrl.tenant = tokenService.getTenant();
      $ctrl.tenantName = $ctrl.tenant.name || $ctrl.user.businessName;
      $ctrl.showEditProfile = !sessionService.inTenantBusiness();

      $ctrl.helpLink = APP_CONFIG.brandingHelpUri;
      $ctrl.helpTitle = APP_CONFIG.brandingHelpTitle;
      $ctrl.showSupportButton = showSupportButton(APP_CONFIG.hideSupportButton);
      $ctrl.showTenantSwitch =
        APP_CONFIG.enableTenantSwitching &&
        permissionsService.currentUserHasPermission(
          flowinglyConstants.permissions.TENANT_ACCESS
        );
      $ctrl.canOpenSmartSearch =
        APP_CONFIG.enableSmartSearch && !$ctrl.isMobile;
      $ctrl.userCanAccessCreationHub =
        APP_CONFIG.enableCreationHub &&
        permissionsService.currentUserHasAnyOfPermissions([
          FlowinglyPermissions.FLOWMODEL_CREATE,
          FlowinglyPermissions.PROCEDURE_CREATE
        ]);
      $ctrl.showTenantModal = false;
      $ctrl.openTenantModal = () => {
        $ctrl.showTenantModal = true;
      };
      $ctrl.closeTenantModal = () => {
        $ctrl.showTenantModal = false;
      };
      $ctrl.navigateToCreationHub = () => {
        if ($state.current.name === STATE_MAPPINGS.creationHub) {
          return;
        }
        $state.go(STATE_MAPPINGS.creationHub);
      };

      function showSupportButton(configHide) {
        return (
          !configHide ||
          permissionsService.currentUserHasPermission(
            flowinglyConstants.permissions.SUPPORT_ACCESS
          )
        );
      }

      function editProfile() {
        if (!$ctrl.showEditProfile) {
          return;
        }
        const userData = {};
        angular.copy($ctrl.user, userData);

        dialogService.showDialog({
          template:
            'Client/runner.setup/runner.setup.users/runner.setup.users.dialog.tmpl.html',
          controller: 'userDialogController',
          appendClassName: 'ngdialog-normal',
          scope: $scope,
          data: userData
        });
      }

      function logout() {
        webPushService.removeUsersLocalSubscription().finally(() => {
          $ctrl.onLogoutMethod();
          $ctrl.openUserMenu = false;
        });
      }
    }
  ]
});
